import { useEffect, useRef, useState } from "react";
import { PencilSimpleIcon, CheckIcon, XIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

import { INPUT_CLS } from "./Form";
import api, { formatApiError } from "../../lib/api";

/**
 * InlineEditableField
 * ----------------------------------------------------------------
 * Card-style field with a read mode and an inline edit mode.
 *
 * Interaction:
 *  - Double-click the value to enter edit mode (when `canEdit`).
 *  - Enter to save (single-line), Cmd/Ctrl+Enter on textarea.
 *  - Escape to cancel.
 *  - Blur auto-saves if value changed.
 *
 * Props:
 *  - label, value, name, mono, className
 *  - canEdit: boolean — gated by role
 *  - endpoint: e.g. "/customers/abc123"
 *  - method: "patch" (default) | "put"
 *  - as: "input" (default) | "textarea" | "select" | "date"
 *  - type: input type ("text" | "email" | "tel")
 *  - options: [{value,label}] for as="select"
 *  - placeholder
 *  - onSaved(updatedDoc): callback with the server response
 *  - format(value): optional display formatter
 *  - coerce(rawValue): optional value-coercion before PATCH (e.g. Number)
 */
export default function InlineEditableField({
  label,
  value,
  name,
  mono = false,
  className = "",
  canEdit = false,
  endpoint,
  method = "patch",
  as = "input",
  type = "text",
  options = [],
  placeholder = "—",
  onSaved,
  format,
  coerce,
  testIdPrefix,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const skipBlurRef = useRef(false);

  useEffect(() => {
    if (!editing) setDraft(value ?? "");
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (as === "input") inputRef.current.select();
    }
  }, [editing, as]);

  const startEdit = () => {
    if (!canEdit || busy) return;
    setDraft(value ?? "");
    setEditing(true);
  };

  const cancel = () => {
    skipBlurRef.current = true;
    setDraft(value ?? "");
    setEditing(false);
    setTimeout(() => {
      skipBlurRef.current = false;
    }, 50);
  };

  const save = async () => {
    const trimmed =
      typeof draft === "string" && as !== "select" ? draft.trim() : draft;
    const original = value ?? "";
    if (trimmed === original) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      const raw = trimmed === "" ? null : trimmed;
      const outgoing = coerce && raw !== null ? coerce(raw) : raw;
      const payload = { [name]: outgoing };
      const { data } = await api[method](endpoint, payload);
      onSaved?.(data);
      toast.success(`${label} updated`);
      setEditing(false);
    } catch (e) {
      toast.error(formatApiError(e) || `Could not update ${label.toLowerCase()}`);
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      cancel();
      return;
    }
    if (e.key === "Enter") {
      if (as === "textarea" && !(e.metaKey || e.ctrlKey)) return;
      e.preventDefault();
      save();
    }
  };

  const onBlur = () => {
    if (skipBlurRef.current) return;
    save();
  };

  const tid = testIdPrefix ? `${testIdPrefix}-${name}` : undefined;
  const displayValue = format ? format(value) : value;

  return (
    <div
      className={`group relative bg-white border border-slate-200 rounded-sm p-4 transition ${
        canEdit && !editing
          ? "hover:border-amber-400 hover:shadow-sm cursor-text"
          : ""
      } ${className}`}
      onDoubleClick={startEdit}
      data-testid={tid ? `${tid}-card` : undefined}
    >
      <div className="flex items-center justify-between">
        <div className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-500">
          {label}
        </div>
        {canEdit && !editing && (
          <button
            type="button"
            onClick={startEdit}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-amber-600"
            title={`Edit ${label.toLowerCase()} (double-click)`}
            data-testid={tid ? `${tid}-edit-btn` : undefined}
            aria-label={`Edit ${label}`}
          >
            <PencilSimpleIcon size={12} weight="bold" />
          </button>
        )}
      </div>

      {!editing && (
        <div
          className={`mt-1.5 text-sm ${mono ? "font-data" : ""} text-slate-900 break-words whitespace-pre-wrap`}
          data-testid={tid ? `${tid}-value` : undefined}
        >
          {displayValue || <span className="text-slate-400">{placeholder}</span>}
        </div>
      )}

      {editing && (
        <div className="mt-1.5">
          {as === "textarea" && (
            <textarea
              ref={inputRef}
              rows={3}
              value={draft ?? ""}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              onBlur={onBlur}
              disabled={busy}
              className={INPUT_CLS}
              data-testid={tid ? `${tid}-input` : undefined}
            />
          )}
          {as === "select" && (
            <select
              ref={inputRef}
              value={draft ?? ""}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              onBlur={onBlur}
              disabled={busy}
              className={INPUT_CLS}
              data-testid={tid ? `${tid}-input` : undefined}
            >
              {options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          )}
          {as === "date" && (
            <input
              ref={inputRef}
              type="date"
              value={draft ?? ""}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              onBlur={onBlur}
              disabled={busy}
              className={`${INPUT_CLS} font-data`}
              data-testid={tid ? `${tid}-input` : undefined}
            />
          )}
          {as === "input" && (
            <input
              ref={inputRef}
              type={type}
              value={draft ?? ""}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              onBlur={onBlur}
              disabled={busy}
              className={`${INPUT_CLS} ${mono ? "font-data" : ""}`}
              data-testid={tid ? `${tid}-input` : undefined}
            />
          )}
          <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-slate-100 border border-slate-200 rounded font-data text-[9px]">
                  {as === "textarea" ? "Ctrl+Enter" : "Enter"}
                </kbd>
                save
              </span>
              <span className="inline-flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-slate-100 border border-slate-200 rounded font-data text-[9px]">
                  Esc
                </kbd>
                cancel
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={cancel}
                disabled={busy}
                className="p-1 text-slate-500 hover:text-slate-800"
                title="Cancel"
                data-testid={tid ? `${tid}-cancel-btn` : undefined}
                aria-label="Cancel"
              >
                <XIcon size={14} />
              </button>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={save}
                disabled={busy}
                className="p-1 text-emerald-600 hover:text-emerald-800 disabled:opacity-50"
                title="Save"
                data-testid={tid ? `${tid}-save-btn` : undefined}
                aria-label="Save"
              >
                <CheckIcon size={14} weight="bold" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
